import { ApiError } from '@/lib/api-client'
import { mostrarAviso } from '@/components/ui/toaster'

/**
 * erro-visivel — traduz falha técnica em aviso ao usuário, sem PII.
 *
 * Regra: a mensagem exibida NUNCA carrega dado pessoal. Mensagem do backend é
 * usada apenas para 4xx (contrato do domínio, texto controlado por nós); 5xx e
 * falha de rede caem em texto genérico, porque o corpo pode conter qualquer
 * coisa.
 */

const GENERICO = 'Não foi possível concluir a ação. Tente novamente.'

/**
 * ehSemPermissao é o ÚNICO lugar que decide "isto é falta de acesso" (#425).
 * Comparar `status === 403` na mão em cada tela foi o que fez as 42 telas
 * tratarem permissão negada como falha de sistema.
 */
export function ehSemPermissao(erro: unknown): boolean {
  return erro instanceof ApiError && erro.status === 403
}

/** 401 e 403 não viram aviso: o refresh/gate de sessão e o estado de permissão
 *  da própria tela já respondem por eles (ver #425). */
function silencioso(erro: unknown): boolean {
  return (erro instanceof ApiError && erro.status === 401) || ehSemPermissao(erro)
}

export function mensagemDeErro(erro: unknown): string {
  if (erro instanceof ApiError) {
    if (erro.status >= 400 && erro.status < 500 && erro.message) return erro.message
    return GENERICO
  }
  return GENERICO
}

/** avisarErro publica o aviso, a menos que o erro já tenha tratamento próprio. */
export function avisarErro(erro: unknown, contexto?: string): void {
  if (silencioso(erro)) return
  mostrarAviso(contexto ?? 'Algo deu errado', mensagemDeErro(erro))
}

/**
 * registrarErrosGlobais captura o que escapa de handler e de Promise — erros
 * que boundary de React não alcança, por rodarem fora do ciclo de render.
 * Devolve a função de limpeza.
 */
export function registrarErrosGlobais(): () => void {
  function aoErro(evento: ErrorEvent) {
    console.error('[erro-global]', evento.error ?? evento.message)
    mostrarAviso('Algo deu errado', GENERICO)
  }

  function aoRejeitar(evento: PromiseRejectionEvent) {
    if (silencioso(evento.reason)) return
    console.error('[promise-rejeitada]', evento.reason)
    mostrarAviso('Algo deu errado', mensagemDeErro(evento.reason))
  }

  window.addEventListener('error', aoErro)
  window.addEventListener('unhandledrejection', aoRejeitar)
  return () => {
    window.removeEventListener('error', aoErro)
    window.removeEventListener('unhandledrejection', aoRejeitar)
  }
}
